import type { ClientIndicators, DashboardMode } from '@/types'
import { getStatusLevel } from '@/utils/indicators'
import { PendingList } from './PendingList'

interface ClientDetailProps {
  indicators: ClientIndicators
  mode: DashboardMode
  onClose: () => void
}

export function ClientDetail({ indicators, mode, onClose }: ClientDetailProps) {
  const { client, totalCards, doneCards, pendingCards, percentage, pendingGroups, error } =
    indicators

  const statusLevel = getStatusLevel(percentage)

  const doneLabel = mode === 'production' ? 'prontos' : 'agendados'
  const titleMode = mode === 'production' ? 'Produção' : 'Agendamento'

  const barColor =
    statusLevel === 'green'
      ? 'bg-green-500'
      : statusLevel === 'yellow'
      ? 'bg-yellow-400'
      : 'bg-red-400'

  const textColor =
    statusLevel === 'green'
      ? 'text-green-600'
      : statusLevel === 'yellow'
      ? 'text-yellow-600'
      : 'text-red-500'

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-8 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-brand-beige bg-brand-black text-white">
          <div>
            <p className="text-xs text-brand-taupe uppercase tracking-widest mb-1">
              {titleMode}
            </p>
            <h2 className="text-lg font-semibold">{client.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl
                       border border-white/20 text-white hover:bg-white/10 transition-colors"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        {error ? (
          <div className="px-6 py-8 text-center text-red-500 text-sm">{error}</div>
        ) : (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-3 divide-x divide-brand-beige border-b border-brand-beige">
              <Stat label="Total" value={totalCards} />
              <Stat label={doneLabel.charAt(0).toUpperCase() + doneLabel.slice(1)} value={doneCards} highlight />
              <Stat label="Pendentes" value={pendingCards} />
            </div>

            {/* Progress bar */}
            <div className="px-6 py-5 border-b border-brand-beige">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-brand-taupe font-medium uppercase tracking-wider">
                  Progresso
                </span>
                <span className={`text-2xl font-semibold ${textColor}`}>{percentage}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-brand-beige overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-brand-taupe">
                {doneCards} de {totalCards} conteúdos {doneLabel}
              </p>
            </div>

            {/* Pending list */}
            <div className="px-6 py-5">
              <h3 className="text-xs font-semibold text-brand-taupe uppercase tracking-wider mb-4">
                {totalCards === 0
                  ? 'Nenhum card neste mês'
                  : pendingCards === 0
                  ? 'Pendências'
                  : `Pendências (${pendingCards})`}
              </h3>

              {totalCards === 0 ? (
                <div className="text-center py-6 text-brand-taupe text-sm">
                  Nenhum card encontrado para este cliente no mês selecionado.
                </div>
              ) : (
                <PendingList groups={pendingGroups} mode={mode} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: number
  highlight?: boolean
}) {
  return (
    <div className="px-6 py-4 text-center">
      <p className="text-xs text-brand-taupe uppercase tracking-wider mb-1">{label}</p>
      <p
        className={`text-2xl font-semibold ${highlight ? 'text-green-600' : 'text-brand-black'}`}
      >
        {value}
      </p>
    </div>
  )
}
